import Link from "next/link";
import { ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils/cn";

type CommonProps = {
  variant?: "primary" | "secondary" | "inverse";
  withArrow?: boolean;
  className?: string;
  children: React.ReactNode;
};

type LinkProps = CommonProps & {
  href: string;
  type?: never;
  disabled?: never;
  "aria-disabled"?: boolean;
};

type NativeProps = CommonProps & {
  href?: undefined;
  type?: "button" | "submit";
  disabled?: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  "aria-disabled"?: boolean;
};

export type ButtonProps = LinkProps | NativeProps;

const base =
  "group inline-flex items-center justify-center gap-2 rounded-control font-sans font-medium text-[0.9375rem] leading-none px-7 min-h-[52px] md:min-h-12 transition-colors duration-[180ms] ease-[cubic-bezier(.22,1,.36,1)] select-none";

const variants = {
  primary: "bg-ink text-surface hover:bg-ink-deep active:translate-y-px",
  secondary:
    "border border-line-strong text-ink hover:bg-surface-alt hover:border-ink active:translate-y-px",
  inverse: "bg-surface text-ink hover:bg-surface-alt active:translate-y-px",
} as const;

export function Button(props: ButtonProps) {
  const {
    variant = "primary",
    withArrow = false,
    className,
    children,
    ...rest
  } = props;

  const classes = cn(base, variants[variant], className);

  const content = (
    <>
      <span>{children}</span>
      {withArrow ? (
        <ArrowRight
          aria-hidden="true"
          strokeWidth={1.5}
          className="h-4 w-4 transition-transform duration-[180ms] ease-[cubic-bezier(.22,1,.36,1)] group-hover:translate-x-1"
        />
      ) : null}
    </>
  );

  if ("href" in rest && rest.href) {
    const { href, ...anchorRest } = rest;
    if (href.startsWith("/") || href.startsWith("#")) {
      return (
        <Link href={href} className={classes} {...anchorRest}>
          {content}
        </Link>
      );
    }
    return (
      <a href={href} className={classes} {...anchorRest}>
        {content}
      </a>
    );
  }

  const { type = "button", disabled, ...buttonRest } = rest;
  return (
    <button
      type={type}
      disabled={disabled}
      className={cn(classes, disabled && "opacity-45 cursor-not-allowed")}
      {...buttonRest}
    >
      {content}
    </button>
  );
}
